import { addDoc, collection } from "firebase/firestore";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { Close } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";

interface UploadMagazineTabProps {
  onSuccess?: () => void;
}

function normalizeYear(value: string) {
  return value.replace(/[^0-9]/g, "").slice(0, 4);
}

export default function UploadMagazineTab({ onSuccess }: UploadMagazineTabProps) {
  const { showSnackbar } = useFeedback();
  const [title, setTitle] = useState("");
  const [year, setYear] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const submitLockRef = useRef(false);

  async function handleSubmit() {
    if (submitLockRef.current) {
      return;
    }

    if (!title.trim() || !year.trim() || !selectedPdfFile) {
      showSnackbar("Please fill title, year, and upload the PDF file");
      return;
    }

    try {
      submitLockRef.current = true;
      setIsSaving(true);
      const timestamp = new Date().toISOString();
      const magazineYear = normalizeYear(year);

      let coverImage = "";
      if (selectedCoverImage) {
        const coverRef = ref(storage, `yuvagaaz/magazine-covers/${Date.now()}-${selectedCoverImage.name}`);
        await uploadBytes(coverRef, selectedCoverImage);
        coverImage = await getDownloadURL(coverRef);
      }

      const pdfRef = ref(storage, `yuvagaaz/magazines/${Date.now()}-${selectedPdfFile.name}`);
      await uploadBytes(pdfRef, selectedPdfFile);
      const pdfUrl = await getDownloadURL(pdfRef);

      await addDoc(collection(firestore, "yuvagaaz_magazines"), {
        title: title.trim(),
        year: magazineYear,
        releaseDate: releaseDate.trim() || `${magazineYear}-01-01`,
        coverImage,
        pdfUrl,
        createdAt: timestamp,
        updatedAt: timestamp,
      });

      setTitle("");
      setYear("");
      setReleaseDate("");
      setSelectedCoverImage(null);
      setSelectedPdfFile(null);
      showSnackbar("Magazine added successfully");
      onSuccess?.();
    } catch (error) {
      console.error("Error adding Yuvagaaz magazine", error);
      showSnackbar("Failed to add magazine");
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Year"
            value={year}
            onChange={(event) => setYear(normalizeYear(event.target.value))}
            inputProps={{ maxLength: 4 }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Release Date"
            type="date"
            value={releaseDate}
            onChange={(event) => setReleaseDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Cover Image</FormLabel>
            <input
              type="file"
              accept="image/*"
              style={{
                border: "1px solid rgb(181, 176, 176)",
                width: "96%",
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 5,
                marginTop: 8,
              }}
              onChange={(event) => setSelectedCoverImage(event.target.files?.[0] || null)}
            />
            {selectedCoverImage ? (
              <Box mt={2} position="relative" display="inline-block">
                <img
                  src={URL.createObjectURL(selectedCoverImage)}
                  alt="Magazine cover preview"
                  style={{ maxWidth: "220px", maxHeight: "180px", borderRadius: 8 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setSelectedCoverImage(null)}
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    color: "#ffffff",
                    backgroundColor: "rgba(0,0,0,0.5)",
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            ) : null}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Magazine PDF</FormLabel>
            <input
              type="file"
              accept="application/pdf"
              style={{
                border: "1px solid rgb(181, 176, 176)",
                width: "96%",
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 5,
                marginTop: 8,
              }}
              onChange={(event) => setSelectedPdfFile(event.target.files?.[0] || null)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Magazine"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
